import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Phone, Mail, Plus, EyeOff, ChevronDown, Radar, Clock } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";
import { fmtDate, relTime } from "@/lib/format";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { CompanyLogo, Skeleton, InlineError } from "@/components/common";
import { EvidenceLabel, ConfidenceChip, ScorePill, ScoreBreakdown, TimingChip, SourceLine, ValueRange, SectionLabel, Chip, kindLabel, IntelStageChip } from "@/components/intel/primitives";
import { useIntel } from "@/components/intel/context";

export function SignalCard({ signal, label, className, testId }) {
  return (
    <div className={cn("rounded-xl border border-black/[0.06] bg-white px-4 py-3.5", className)} data-testid={testId || "signal-card"}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground"><Radar className="h-3.5 w-3.5" /> {label || signal.type}</span>
        <ConfidenceChip level={signal.confidence} />
      </div>
      <div className="mt-1.5 text-[14px] font-semibold leading-snug tracking-[-0.01em]" data-testid="signal-card-title">{signal.title}</div>
      <SourceLine signal={signal} className="mt-1.5" />
      {signal.confirmed_fact ? (
        <div className="mt-3 flex items-start gap-2.5">
          <EvidenceLabel kind="fact" compact className="mt-0.5" />
          <p className="text-[13px] leading-relaxed text-foreground/90">{signal.confirmed_fact}</p>
        </div>
      ) : null}
      {signal.ai_interpretation ? (
        <div className="mt-2 flex items-start gap-2.5">
          <EvidenceLabel kind="inference" compact className="mt-0.5" />
          <p className="text-[13px] leading-relaxed text-muted-foreground">{signal.ai_interpretation}</p>
        </div>
      ) : null}
    </div>
  );
}

/** Deep-dive for one project: all evidence, hypothesis, fit, roles, score and actions - without leaving the page. */
export function SignalSheet({ open, onOpenChange, projectId, initialView }) {
  const navigate = useNavigate();
  const intel = useIntel();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showScore, setShowScore] = useState(false);

  useEffect(() => {
    if (!open || !projectId) return;
    setError(null);
    setData(initialView ? { view: initialView, company: initialView.company, actions: [] } : null);
    let alive = true;
    api.get(`/intel/projects/${projectId}`).then((r) => alive && setData(r.data)).catch((e) => alive && setError(errMsg(e)));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId, intel.version]);

  const v = data?.view;
  const c = v?.company || data?.company;
  const signalTypes = intel.signalTypes || {};

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 bg-background p-0 sm:max-w-[640px]" data-testid="signal-sheet">
        <SheetHeader className="space-y-0 border-b border-black/[0.06] bg-white px-5 pb-4 pt-5">
          {v ? (
            <>
              <div className="flex items-start gap-3 pr-8">
                <CompanyLogo name={c?.name} domain={c?.website} size="md" />
                <div className="min-w-0 flex-1">
                  <button type="button" onClick={() => { onOpenChange(false); navigate(`/intel/companies/${v.company_id}`); }} className="group inline-flex max-w-full items-center gap-1 text-left text-[12.5px] font-medium text-muted-foreground hover:text-primary" data-testid="signal-sheet-company-link">
                    <span className="truncate">{c?.name}</span> <ArrowUpRight className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                  </button>
                  <SheetTitle className="text-[18px] font-semibold leading-snug tracking-[-0.015em]" data-testid="signal-sheet-project-name">{v.project_name}</SheetTitle>
                  <SheetDescription className="text-[12.5px]">{[v.project_type_label, c?.segment, [c?.city, c?.country].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}</SheetDescription>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                <ScorePill score={v.score} breakdown={v.score_breakdown} testId="signal-sheet-score-value" />
                <TimingChip timing={v.timing} phase={v.phase} />
                <ConfidenceChip level={v.confidence} />
                <Chip className="text-[11.5px]">{kindLabel(v.kind)}</Chip>
                {v.opportunity ? <IntelStageChip stage={v.opportunity.stage} /> : null}
              </div>
            </>
          ) : (
            <div className="space-y-2 pr-8"><Skeleton className="h-3 w-24" /><Skeleton className="h-5 w-2/3" /><Skeleton className="h-3 w-1/2" /></div>
          )}
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 scrollbar-thin">
          {error ? <InlineError testId="signal-sheet-error">{error}</InlineError> : null}
          {!v ? (
            <div className="space-y-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 w-full" />)}</div>
          ) : (
            <div className="space-y-6">
              <section>
                <SectionLabel right={<EvidenceLabel kind="hypothesis" />}>Why it matters</SectionLabel>
                <p className="text-[14px] leading-relaxed text-foreground/90" data-testid="signal-sheet-hypothesis">{v.hypothesis}</p>
                <div className="mt-3 flex items-start gap-2.5 rounded-xl border border-black/[0.06] bg-white px-3.5 py-3">
                  <Clock className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center justify-between gap-2"><span className="text-[12px] font-semibold">Why now</span><EvidenceLabel kind="inference" compact /></div>
                    <p className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">{v.why_now}</p>
                  </div>
                </div>
              </section>

              <section>
                <SectionLabel right={<span className="text-[11px] text-muted-foreground">{v.signals.length} signal{v.signals.length === 1 ? "" : "s"}</span>}>Evidence</SectionLabel>
                <div className="space-y-2.5" data-testid="signal-sheet-evidence-list">
                  {v.signals.map((s) => <SignalCard key={s.id} signal={s} label={signalTypes[s.type] || s.type} />)}
                </div>
              </section>

              <section className="grid gap-4 sm:grid-cols-2">
                <div>
                  <SectionLabel right={<EvidenceLabel kind="hypothesis" compact />}>Potential need</SectionLabel>
                  <div className="flex flex-wrap gap-1.5">{(v.needs || []).map((n) => <Chip key={n}>{n}</Chip>)}</div>
                </div>
                <div>
                  <SectionLabel right={<EvidenceLabel kind="hypothesis" compact />}>Estimated range</SectionLabel>
                  <div className="text-[18px]"><ValueRange label={v.value_label} testId="signal-sheet-value-range" /></div>
                  <div className="text-[11.5px] text-muted-foreground">Estimate for prioritisation - not a quote.</div>
                </div>
              </section>

              <section>
                <SectionLabel right={<EvidenceLabel kind="hypothesis" compact />}>What Mediability could deliver</SectionLabel>
                <div className="divide-y divide-black/[0.05] rounded-xl border border-black/[0.06] bg-white">
                  {(v.solutions || []).map((s) => (
                    <div key={s.category} className="px-4 py-2.5">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-[13.5px] font-semibold">{s.category}</span>
                        <span className="text-[11.5px] text-muted-foreground">{(s.vendors || []).join(" · ")}</span>
                      </div>
                      <div className="mt-0.5 text-[12.5px] text-muted-foreground">{s.why}</div>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <SectionLabel right={<EvidenceLabel kind="inference" compact />}>Who to contact (roles)</SectionLabel>
                <div className="space-y-1.5">
                  {(v.roles || []).map((r) => (
                    <div key={r.role} className="flex gap-2.5 text-[13px]"><span className="mt-[8px] h-1 w-1 shrink-0 rounded-full bg-foreground/50" /><span><span className="font-semibold">{r.role}</span> <span className="text-muted-foreground">— {r.why}</span></span></div>
                  ))}
                </div>
                {c?.contacts?.length ? (
                  <div className="mt-3 rounded-xl border border-black/[0.06] bg-white px-4 py-3">
                    <SectionLabel right={<EvidenceLabel kind="fact" compact />}>Known contacts</SectionLabel>
                    {c.contacts.map((k, i) => <div key={i} className="text-[13px]"><span className="font-medium">{k.name}</span> <span className="text-muted-foreground">· {k.role}</span></div>)}
                  </div>
                ) : null}
              </section>

              <section>
                <SectionLabel>Questions to validate the hypothesis</SectionLabel>
                <ol className="space-y-1.5 text-[13px]">
                  {(v.validation_questions || []).map((q, i) => <li key={i} className="flex gap-2.5"><span className="font-mono text-[11.5px] tabular text-muted-foreground">{String(i + 1).padStart(2, "0")}</span><span>{q}</span></li>)}
                </ol>
              </section>

              <Collapsible open={showScore} onOpenChange={setShowScore}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="-ml-2 gap-1.5 text-muted-foreground" data-testid="signal-sheet-score-breakdown-toggle">Why this score? <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", showScore && "rotate-180")} /></Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2"><ScoreBreakdown breakdown={v.score_breakdown} score={v.score} /></CollapsibleContent>
              </Collapsible>

              {data?.actions?.length ? (
                <section>
                  <SectionLabel>Recent activity</SectionLabel>
                  <div className="space-y-1.5 text-[12.5px]">
                    {data.actions.slice(0, 5).map((a) => <div key={a.id} className="flex items-center justify-between gap-3"><span className="truncate"><span className="font-medium">{a.user_name?.split(" ")[0]}</span> <span className="text-muted-foreground">{a.type.replace(/_/g, " ")}{a.note ? ` · ${a.note}` : ""}</span></span><span className="shrink-0 font-mono text-[11px] text-muted-foreground" title={fmtDate(a.created_at)}>{relTime(a.created_at)}</span></div>)}
                  </div>
                </section>
              ) : null}
            </div>
          )}
        </div>

        {v ? (
          <div className="flex flex-wrap items-center gap-2 border-t border-black/[0.06] bg-white px-5 py-3">
            <Button variant="outline" size="sm" onClick={() => { onOpenChange(false); navigate(`/intel/companies/${v.company_id}`); }} data-testid="signal-sheet-open-company-button">Open company <ArrowUpRight className="h-3.5 w-3.5" /></Button>
            <Button variant="soft" size="sm" onClick={() => intel.openCopilot(v, "brief")} data-testid="signal-sheet-call-brief-button"><Phone className="h-3.5 w-3.5" /> Call brief</Button>
            <Button variant="soft" size="sm" onClick={() => intel.openCopilot(v, "outreach")} data-testid="signal-sheet-outreach-button"><Mail className="h-3.5 w-3.5" /> Outreach</Button>
            <div className="ml-auto flex items-center gap-2">
              <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => { intel.dismiss(v); onOpenChange(false); }} data-testid="signal-sheet-dismiss-button"><EyeOff className="h-3.5 w-3.5" /> Dismiss</Button>
              <Button size="sm" onClick={() => intel.openCreate(v)} data-testid="signal-sheet-create-opportunity-button"><Plus className="h-3.5 w-3.5" /> {v.opportunity ? (v.opportunity.linked_opportunity_id ? "In pipeline" : "Hand off") : "Create opportunity"}</Button>
            </div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
